import { useCallback, useMemo } from 'react';

import { useCasesForMasterclass } from '@/modules/case-selector/hooks/useCasesForMasterclass';

import { CaseMetadata } from '../types';

interface UseCasesConfigOptions {
  masterclassId?: string;
}

/**
 * Hook to get case metadata from GraphQL.
 *
 * When masterclassId is provided, fetches cases from GraphQL.
 * When masterclassId is not provided, returns an empty array.
 *
 * Note: Case content (configUrl, assetsBaseUrl) is loaded separately via
 * stringifiedContent field in GraphQL, not via S3 URLs.
 */
export const useCasesConfig = (options?: UseCasesConfigOptions) => {
  const { masterclassId } = options || {};

  // Use GraphQL when masterclassId is provided
  const graphqlResult = useCasesForMasterclass(masterclassId);

  // Map GraphQL data to CaseMetadata format
  const cases: CaseMetadata[] = useMemo(() => {
    if (!masterclassId) {
      // No masterclassId provided - return empty array
      // Components should redirect to masterclass selection
      return [];
    }
    // Map GraphQL MasterclassCaseMetadata to CaseMetadata
    const S3_BASE = 'https://livelinx-app-assets.s3.eu-central-1.amazonaws.com/patientCases';

    return graphqlResult.cases.map((c) => ({
      id: c.id, // Use database ID as the primary identifier
      legacyCaseKey: c.legacyCaseId, // Keep legacy key for S3 asset paths
      title: c.name,
      // Case content loads from stringifiedContent via GraphQL, but assets still in S3
      configUrl: '', // Not used - content from GraphQL
      assetsBaseUrl: `${S3_BASE}/cases/${c.legacyCaseId}/`,
      // Note: certificationUrl removed - certification questions now fetched via GraphQL
    }));
  }, [masterclassId, graphqlResult.cases]);

  const getCaseMetadata = useCallback(
    (caseId: string): CaseMetadata | undefined => {
      return cases.find((c) => c.id === caseId);
    },
    [cases]
  );

  const getCaseMetadataByLegacyKey = useCallback(
    (legacyCaseKey: string): CaseMetadata | undefined => {
      return cases.find((c) => c.legacyCaseKey === legacyCaseKey);
    },
    [cases]
  );

  return {
    cases,
    casesConfig: { cases }, // For backward compatibility
    masterclassTitle: graphqlResult.masterclassTitle,
    loading: masterclassId ? graphqlResult.loading : false,
    error: masterclassId ? graphqlResult.error : undefined,
    getCaseMetadata,
    getCaseMetadataByLegacyKey,
  };
};
